import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel
} from '@microsoft/signalr';
import {
  PATIENT_TRACKING_HUB_PATH,
  type PatientRosterParticipant,
  type PatientTrackingRowChange
} from './patientTrackingTypes';

const HEARTBEAT_MS = 4000;

export interface PatientTrackingClientOptions {
  boardKey: string;
  onRosterChanged: (roster: PatientRosterParticipant[]) => void;
  onRowUpserted: (change: PatientTrackingRowChange) => void;
  onRowRemoved: (change: PatientTrackingRowChange) => void;
  onConnectedChange?: (connected: boolean) => void;
}

export interface PatientTrackingClient {
  start: () => Promise<void>;
  stop: () => Promise<void>;
  beginEditRow: (rowId: string) => void;
  endEditRow: () => void;
  setListEditing: (listEditing: boolean) => void;
}

function normalizeParticipant(raw: Record<string, unknown>): PatientRosterParticipant {
  return {
    userId: String(raw.userId ?? raw.UserId ?? ''),
    displayName: String(raw.displayName ?? raw.DisplayName ?? ''),
    upn: (raw.upn ?? raw.Upn ?? null) as string | null,
    connections: Number(raw.connections ?? raw.Connections ?? 0),
    editingRowId: (raw.editingRowId ?? raw.EditingRowId ?? null) as string | null,
    listEditing: Boolean(raw.listEditing ?? raw.ListEditing)
  };
}

function normalizeRoster(raw: unknown): PatientRosterParticipant[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((row) =>
    normalizeParticipant(typeof row === 'object' && row != null ? (row as Record<string, unknown>) : {})
  );
}

function normalizeChange(raw: unknown): PatientTrackingRowChange {
  const r = (typeof raw === 'object' && raw != null ? raw : {}) as Record<string, unknown>;
  return {
    boardKey: String(r.boardKey ?? r.BoardKey ?? ''),
    changeType: String(r.changeType ?? r.ChangeType ?? ''),
    row: (r.row ?? r.Row ?? null) as unknown,
    rowId: (r.rowId ?? r.RowId ?? null) as string | null,
    changedByUserId: (r.changedByUserId ?? r.ChangedByUserId ?? null) as string | null,
    changedByDisplayName: (r.changedByDisplayName ?? r.ChangedByDisplayName ?? null) as string | null,
    changedAtUtc: String(r.changedAtUtc ?? r.ChangedAtUtc ?? '')
  };
}

/**
 * Thin SignalR wrapper for one tracker board. Joins the board, heartbeats presence, surfaces roster
 * + row-change pushes, and re-joins after reconnects. Same-origin cookie auth; the Vite dev proxy
 * forwards /hubs with the WS upgrade.
 */
export function createPatientTrackingClient(
  options: PatientTrackingClientOptions
): PatientTrackingClient {
  const { boardKey, onRosterChanged, onRowUpserted, onRowRemoved, onConnectedChange } = options;

  let connection: HubConnection | null = null;
  let heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  let disposed = false;

  const leaveNow = () => {
    const conn = connection;
    if (!conn || conn.state !== HubConnectionState.Connected) return;
    conn.invoke('LeaveAsync').catch(() => undefined);
  };

  const onPageHide = () => leaveNow();

  const stopHeartbeat = () => {
    if (heartbeatTimer != null) {
      clearInterval(heartbeatTimer);
      heartbeatTimer = null;
    }
  };

  const startHeartbeat = (conn: HubConnection) => {
    stopHeartbeat();
    heartbeatTimer = setInterval(() => {
      if (conn.state === HubConnectionState.Connected) {
        conn.invoke('HeartbeatAsync').catch(() => undefined);
      }
    }, HEARTBEAT_MS);
  };

  const join = async (conn: HubConnection) => {
    try {
      const roster = await conn.invoke<unknown>('JoinBoardAsync', boardKey);
      onRosterChanged(normalizeRoster(roster));
    } catch {
      /* roster also arrives via RosterChanged */
    }
  };

  const invoke = (method: string, ...args: unknown[]) => {
    const conn = connection;
    if (!conn || conn.state !== HubConnectionState.Connected) return;
    conn.invoke(method, ...args).catch(() => undefined);
  };

  const start = async () => {
    if (connection || disposed) return;

    const conn = new HubConnectionBuilder()
      .withUrl(PATIENT_TRACKING_HUB_PATH, { withCredentials: true })
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Warning)
      .build();

    conn.on('RosterChanged', (...args: unknown[]) => {
      // Server sends (boardKey, roster); tolerate a single-arg roster too.
      if (args.length >= 2 && String(args[0] ?? '') !== boardKey) return;
      const roster = args.length >= 2 ? args[1] : args[0];
      onRosterChanged(normalizeRoster(roster));
    });
    conn.on('RowUpserted', (change: unknown) => onRowUpserted(normalizeChange(change)));
    conn.on('RowRemoved', (change: unknown) => onRowRemoved(normalizeChange(change)));

    conn.onreconnected(() => {
      onConnectedChange?.(true);
      void join(conn);
    });
    conn.onreconnecting(() => onConnectedChange?.(false));
    conn.onclose(() => {
      onConnectedChange?.(false);
      stopHeartbeat();
    });

    window.addEventListener('pagehide', onPageHide);
    connection = conn;
    try {
      await conn.start();
      if (disposed) {
        await conn.stop();
        return;
      }
      onConnectedChange?.(true);
      await join(conn);
      startHeartbeat(conn);
    } catch {
      onConnectedChange?.(false);
    }
  };

  const stop = async () => {
    disposed = true;
    window.removeEventListener('pagehide', onPageHide);
    stopHeartbeat();
    const conn = connection;
    connection = null;
    if (!conn) return;
    try {
      if (conn.state === HubConnectionState.Connected) {
        await conn.invoke('LeaveAsync').catch(() => undefined);
      }
    } finally {
      await conn.stop().catch(() => undefined);
    }
  };

  return {
    start,
    stop,
    beginEditRow: (rowId: string) => invoke('BeginEditRowAsync', rowId),
    endEditRow: () => invoke('EndEditRowAsync'),
    setListEditing: (listEditing: boolean) => invoke('SetListEditingAsync', listEditing)
  };
}
